/**
 * Carbon Compactors — Buyer Motivation Survey (research, NOT lead gen)
 *
 * Two parts:
 *   1. A site-wide two-question survey in a small bottom-right card (desktop) /
 *      bottom sheet (mobile).
 *   2. One optional "What made you look for a baler now?" box on the success
 *      state of a quote form.
 *
 * WHY A CUSTOM RENDERER
 * The PostHog survey (id below) is type "api": PostHog stores the questions and
 * shows results, but renders nothing. Its native popover cannot do what this
 * needs: save Q1 the moment a row is clicked, keep one 60-second timer across page
 * loads, get out of the way of lead forms and drawers, and write normalized codes
 * as events + person properties. Launch/stop in PostHog is still the on/off
 * switch — this file only shows the survey while it is launched and not ended.
 *
 * EVENTS
 *   survey shown / survey sent / survey dismissed  — PostHog's native survey events.
 *     Q1 sends a PARTIAL response ($survey_completed: false); Q2 completes the same
 *     $survey_submission_id. Abandoning Q2 therefore keeps Q1.
 *   cardboard_baler_reason_selected    reason_code, reason_label, other_text
 *   cardboard_current_process_selected process_code, process_label, other_text
 *   cardboard_baler_why_now            response_text, primary_reason, current_process
 * Every one carries the site's first-touch attribution (window.CCAttribution,
 * same property names as contact_form_submitted) so answers join to ads.
 *
 * PERSON PROPERTIES ($set_once — the first answer is never overwritten)
 *   cardboard_baler_primary_reason, cardboard_current_process (+ _label, _other)
 * posthog.identify() on form submit (cc-posthog-events.js) merges this anonymous
 * person into the lead, so answers given before a quote stay on that person.
 *
 * FREQUENCY (localStorage, per browser)
 *   Answered Q1           -> never asked again (Q2 may continue this session).
 *   Answered both         -> never again.
 *   Closed before answering -> not this session; again after 7 days; never after
 *                            the 2nd close.
 *   Hidden because a drawer/modal/form needed the space -> NOT a dismissal.
 *
 * QA: ?cc_survey=test  shows it regardless of launch state, from a clean slate,
 *     tags every event cc_survey_test=true (dashboard excludes them).
 *     ?cc_survey=off   leaves test mode.
 */
(function () {
  'use strict';

  var SURVEY_ID = '01a0cc12-32b5-0000-e996-0adbfccd195b';
  var SURVEY_NAME = 'Cardboard Baler — Buyer Motivation Survey';
  var Q1_ID = '895e3174-4b09-47b8-a0d3-a05ef94d1f54';
  var Q2_ID = '55a44b24-9c84-493b-b195-78fd511f8df3';

  var SHOW_AFTER_MS = 60000;       // active (tab-visible) time, summed across pages
  var REDISPLAY_DAYS = 7;
  var MAX_DISMISSALS = 2;
  var THANKS_MS = 1200;

  var LS_KEY = 'cc_bms_v1';        // per browser: answers + dismissals
  var SS_KEY = 'cc_bms_sess_v1';   // per tab session: timer, shown, q2 pending

  // Forms that count as a quote. Same prefixes as cc-lead-conversion.js "high".
  var QUOTE_FORMS = ['pdp-msg', 'ContactQuoteForm', 'PickQuoteForm'];

  var Q1 = {
    id: Q1_ID,
    eyebrow: 'Quick question',
    text: 'Why are you looking for a cardboard baler?',
    options: [
      ['dumpster_full', 'Our dumpster fills up too fast'],
      ['dumpster_cost', 'We pay too much for dumpster pickup'],
      ['space', 'Cardboard takes up too much space'],
      ['employee_time', 'Workers spend too much time on cardboard'],
      ['recycling', 'We need to bale cardboard for recycling'],
      ['replace_or_add', 'We need another baler']
    ],
    other: ['other', 'Something else'],
    otherPrompt: 'Tell us what brought you here',
    otherButton: 'Continue'
  };

  var Q2 = {
    id: Q2_ID,
    eyebrow: 'One more',
    text: 'What do you do with your cardboard now?',
    options: [
      ['dumpster_loose', 'Throw it in a dumpster'],
      ['dumpster_broken_down', 'Break it down, then put it in a dumpster'],
      ['stack_for_pickup', 'Stack it for pickup'],
      ['existing_baler', 'Use a baler now']
    ],
    other: ['other', 'Something else'],
    otherPrompt: 'What do you do now?',
    otherButton: 'Send'
  };

  var THANKS = 'Thanks — that helps.';

  // Page elements the card must never sit on top of (until the visitor starts
  // answering). Lead forms, buy box, cart/checkout controls.
  var AVOID = [
    'form[action^="/contact"]',
    'form[action*="/cart/add"]',
    '[name="checkout"]',
    'a[href="/checkout"]',
    '[data-cc-survey-avoid]'
  ].join(',');

  var CHAT = 'shopify-chat, #ShopifyChat, inbox-online-store-chat';

  // ---------------------------------------------------------------- storage

  function read(store, key) {
    try { return JSON.parse(window[store].getItem(key)) || {}; } catch (e) { return {}; }
  }
  function write(store, key, val) {
    try { window[store].setItem(key, JSON.stringify(val)); } catch (e) {}
  }
  var state = read('localStorage', LS_KEY);
  var sess = read('sessionStorage', SS_KEY);
  function saveState() { write('localStorage', LS_KEY, state); }
  function saveSess() { write('sessionStorage', SS_KEY, sess); }

  var params;
  try { params = new URLSearchParams(window.location.search); } catch (e) { params = null; }
  var mode = params && params.get('cc_survey');
  if (mode === 'test') {
    state = {};
    sess = { test: true };
    saveState();
  } else if (mode === 'off') {
    sess.test = false;
  }
  if (!sess.start) sess.start = Date.now();
  saveSess();
  var TEST = !!sess.test;

  // ---------------------------------------------------------------- context

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  // Analysis-only context. Never used to decide who sees the survey.
  // PostHog adds $current_url, $referrer, $device_type, $session_id and the
  // person's $initial_* properties on its own.
  function context() {
    var p = {};
    var a = (window.CCAttribution && window.CCAttribution.getAll) ? window.CCAttribution.getAll() : {};
    for (var k in a) if (Object.prototype.hasOwnProperty.call(a, k)) p[k] = a[k];
    var first = a.first_seen ? Date.parse(a.first_seen) : NaN;
    // Returning = first touch happened well before this tab session began.
    p.is_returning_visitor = isNaN(first) ? null : (sess.start - first > 30 * 60 * 1000);
    p.survey_page_path = window.location.pathname;
    p.survey_active_seconds = Math.round((sess.activeMs || 0) / 1000);
    if (TEST) p.cc_survey_test = true;
    return p;
  }

  function capture(event, props) {
    if (!window.posthog || !window.posthog.capture) return;
    var ctx = context();
    for (var k in props) if (Object.prototype.hasOwnProperty.call(props, k)) ctx[k] = props[k];
    try { window.posthog.capture(event, ctx, { send_instantly: true }); } catch (e) {}
  }

  function surveyBase(extra) {
    var p = {
      $survey_id: SURVEY_ID,
      $survey_name: SURVEY_NAME,
      $survey_questions: [
        { id: Q1_ID, index: 0, question: Q1.text },
        { id: Q2_ID, index: 1, question: Q2.text }
      ]
    };
    for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) p[k] = extra[k];
    return p;
  }

  function compact(obj) {
    var out = {};
    for (var k in obj) if (obj[k] !== null && obj[k] !== undefined && obj[k] !== '') out[k] = obj[k];
    return out;
  }

  // ---------------------------------------------------------------- eligibility

  function eligibleByHistory() {
    if (state.stage === 'done') return false;
    if (state.stage === 'q1') return !!sess.q2Pending;   // only to finish Q2, this session
    if (sess.dismissed) return false;
    if ((state.dismissals || 0) >= MAX_DISMISSALS) return false;
    if (state.lastDismissedAt && Date.now() - state.lastDismissedAt < REDISPLAY_DAYS * 864e5) return false;
    return true;
  }

  // ---------------------------------------------------------------- styles

  var CSS = [
    '.cc-bms{position:fixed;right:20px;bottom:20px;z-index:60;width:380px;max-width:calc(100vw - 32px);box-sizing:border-box;',
    'background:#fff;color:#1D1D1F;border:1px solid #E5E5EA;border-radius:14px;box-shadow:0 10px 30px rgba(0,0,0,.12),0 1px 3px rgba(0,0,0,.06);',
    'padding:22px;font-family:"Helvetica Neue",Helvetica,Arial,sans-serif;font-size:16px;line-height:1.35;text-align:left;',
    'opacity:0;transform:translateY(12px);visibility:hidden;transition:opacity .22s ease,transform .22s ease,visibility 0s linear .22s}',
    '.cc-bms.is-on{opacity:1;transform:none;visibility:visible;transition:opacity .22s ease,transform .22s ease,visibility 0s}',
    '.cc-bms *{box-sizing:border-box}',
    '.cc-bms__x{position:absolute;top:10px;right:10px;width:32px;height:32px;display:flex;align-items:center;justify-content:center;',
    'border:0;border-radius:50%;background:transparent;color:#6E6E73;cursor:pointer;padding:0;margin:0}',
    '.cc-bms__x:hover{background:#F5F5F7;color:#1D1D1F}',
    '.cc-bms__x svg{width:14px;height:14px;display:block}',
    '.cc-bms__eyebrow{margin:0 0 4px;font-size:13px;font-weight:500;color:#6E6E73;letter-spacing:0}',
    '.cc-bms__q{margin:0 36px 14px 0;font-size:18px;font-weight:600;line-height:1.3;letter-spacing:-.2px;color:#1D1D1F;outline:none}',
    '.cc-bms__opts{display:flex;flex-direction:column;gap:8px;margin:0;padding:0}',
    '.cc-bms__opt{display:block;width:100%;min-height:46px;margin:0;padding:12px 14px;text-align:left;cursor:pointer;',
    'background:#F5F5F7;border:1px solid #E5E5EA;border-radius:10px;color:#1D1D1F;font:inherit;font-size:15.5px;font-weight:500;line-height:1.3;',
    'letter-spacing:0;text-transform:none;box-shadow:none;transition:background .12s ease,border-color .12s ease}',
    '.cc-bms__opt:hover{background:#EDEDF0;border-color:#C7C7CC}',
    '.cc-bms__opt[aria-pressed="true"]{background:#fff;border-color:#F9A825;box-shadow:inset 3px 0 0 #F9A825}',
    '.cc-bms__opt:focus-visible,.cc-bms__x:focus-visible,.cc-bms__go:focus-visible,.cc-whynow__go:focus-visible{outline:2px solid #F9A825;outline-offset:2px}',
    '.cc-bms__other{margin-top:10px}',
    '.cc-bms__other[hidden]{display:none}',
    '.cc-bms__label,.cc-whynow__label{display:block;margin:0 0 6px;font-size:14px;font-weight:500;color:#1D1D1F}',
    '.cc-bms__text,.cc-whynow__text{display:block;width:100%;min-height:72px;margin:0;padding:10px 12px;resize:vertical;',
    'border:1px solid #D2D2D7;border-radius:10px;background:#fff;color:#1D1D1F;font:inherit;font-size:16px;line-height:1.4;box-shadow:none}',
    '.cc-bms__text:focus,.cc-whynow__text:focus{outline:none;border-color:#F9A825;box-shadow:0 0 0 3px rgba(249,168,37,.25)}',
    '.cc-bms__go,.cc-whynow__go{display:inline-flex;align-items:center;justify-content:center;min-height:44px;margin:10px 0 0;padding:0 20px;',
    'border:0;border-radius:10px;background:#1D1D1F;color:#fff;font:inherit;font-size:15px;font-weight:600;letter-spacing:0;text-transform:none;cursor:pointer}',
    '.cc-bms__go:hover,.cc-whynow__go:hover{background:#000}',
    '.cc-bms__thanks,.cc-whynow__thanks{display:flex;align-items:center;gap:8px;margin:0;font-size:17px;font-weight:600;color:#1D1D1F}',
    '.cc-bms__tick,.cc-whynow__tick{color:#1F7A43;font-weight:700}',
    '@media (max-width:749px){',
    '.cc-bms{left:16px;right:16px;bottom:0;width:auto;max-width:none;max-height:72vh;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;',
    'border-bottom:0;border-radius:16px 16px 0 0;padding:20px 18px calc(20px + env(safe-area-inset-bottom));box-shadow:0 -6px 24px rgba(0,0,0,.14)}',
    '.cc-bms__x{top:6px;right:6px;width:44px;height:44px}',
    '.cc-bms__q{font-size:17px;margin-right:40px}',
    '.cc-bms__opt{min-height:48px}',
    '}',
    // Shopify Inbox's chat launcher sits 20px from the bottom-right corner (48px
    // tall, above everything). Keep it visible and clickable: lift the desktop
    // card over it, and on mobile leave a strip at the bottom of the sheet for it.
    '.cc-bms--chat{bottom:84px;max-height:calc(100vh - 104px);overflow-y:auto}',
    '@media (max-width:749px){.cc-bms--chat{bottom:0;max-height:72vh;padding-bottom:calc(80px + env(safe-area-inset-bottom))}}',
    '@media (prefers-reduced-motion:reduce){.cc-bms,.cc-bms.is-on{transition:none;transform:none}}',
    '.cc-whynow{margin:14px 0 0;padding:16px;border:1px solid #E5E5EA;border-radius:12px;background:#fff;color:#1D1D1F;text-align:left;',
    'font-family:"Helvetica Neue",Helvetica,Arial,sans-serif}',
    '.cc-whynow__q{margin:0 0 8px;font-size:17px;font-weight:600;line-height:1.3;color:#1D1D1F}',
    '.cc-whynow__opt{margin:0 0 8px;font-size:13px;color:#6E6E73}'
  ].join('');

  function injectCss() {
    if (document.getElementById('cc-bms-css')) return;
    var s = document.createElement('style');
    s.id = 'cc-bms-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function el(tag, attrs, text) {
    var n = document.createElement(tag);
    for (var k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k)) n.setAttribute(k, attrs[k]);
    if (text) n.textContent = text;
    return n;
  }

  function shuffled(list) {
    var a = list.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  // ---------------------------------------------------------------- survey UI

  var root = null;
  var body = null;
  var started = false;      // visitor has clicked inside the survey
  var finished = false;     // closed / thanked on this page
  var visible = false;

  function build() {
    injectCss();
    root = el('div', { 'class': 'cc-bms', id: 'cc-bms', role: 'region', 'aria-label': 'Quick question' });
    var x = el('button', { type: 'button', 'class': 'cc-bms__x', 'aria-label': 'Close' });
    x.innerHTML = '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
    x.addEventListener('click', dismiss);
    root.appendChild(x);
    body = el('div', {});
    root.appendChild(body);
    root.addEventListener('pointerdown', function () { started = true; });
    root.addEventListener('keydown', function (e) { if (e.key === 'Escape') dismiss(); });
    document.body.appendChild(root);
  }

  function renderQuestion(q, onPick) {
    body.innerHTML = '';
    var headId = 'cc-bms-q-' + q.id.slice(0, 8);
    body.appendChild(el('p', { 'class': 'cc-bms__eyebrow' }, q.eyebrow));
    var h = el('h2', { 'class': 'cc-bms__q', id: headId, tabindex: '-1' }, q.text);
    body.appendChild(h);
    var list = el('div', { 'class': 'cc-bms__opts', role: 'group', 'aria-labelledby': headId });
    var order = shuffled(q.options).concat([q.other]);
    var otherWrap = el('div', { 'class': 'cc-bms__other', hidden: '' });
    var taId = headId + '-other';
    otherWrap.appendChild(el('label', { 'class': 'cc-bms__label', 'for': taId }, q.otherPrompt));
    var ta = el('textarea', { 'class': 'cc-bms__text', id: taId, rows: '3', maxlength: '500' });
    otherWrap.appendChild(ta);
    var go = el('button', { type: 'button', 'class': 'cc-bms__go' }, q.otherButton);
    otherWrap.appendChild(go);

    var buttons = [];
    order.forEach(function (opt, i) {
      var b = el('button', { type: 'button', 'class': 'cc-bms__opt', 'aria-pressed': 'false', 'data-code': opt[0] }, opt[1]);
      b.addEventListener('click', function () {
        started = true;
        buttons.forEach(function (o) { o.setAttribute('aria-pressed', o === b ? 'true' : 'false'); });
        if (opt[0] === 'other') {
          otherWrap.hidden = false;
          root.dataset.otherOpen = q.id;
          ta.focus();
          return;
        }
        otherWrap.hidden = true;
        delete root.dataset.otherOpen;
        onPick(opt[0], opt[1], null, i + 1);
      });
      buttons.push(b);
      list.appendChild(b);
    });
    go.addEventListener('click', function () {
      delete root.dataset.otherOpen;
      onPick('other', q.other[1], (ta.value || '').trim(), order.length);
    });
    body.appendChild(list);
    body.appendChild(otherWrap);
    return h;
  }

  function focusIfInside(target) {
    if (root && root.contains(document.activeElement) && target) {
      try { target.focus({ preventScroll: true }); } catch (e) {}
    }
  }

  function showQ1() {
    renderQuestion(Q1, function (code, label, other, position) {
      answerQ1(code, label, other, position);
      showQ2();
    });
  }

  function showQ2() {
    var h = renderQuestion(Q2, function (code, label, other, position) {
      answerQ2(code, label, other, position);
      showThanks();
    });
    focusIfInside(h);
  }

  function answerQ1(code, label, other, position) {
    state.stage = 'q1';
    state.submissionId = state.submissionId || uuid();
    state.reason = { code: code, label: label, other: other || null };
    state.answeredAt = new Date().toISOString();
    saveState();
    sess.q2Pending = true;
    saveSess();

    var sent = {
      $survey_submission_id: state.submissionId,
      $survey_completed: false,
      $survey_partially_completed: true
    };
    sent['$survey_response_' + Q1_ID] = (code === 'other' && other) ? other : label;
    capture('survey sent', surveyBase(sent));

    capture('cardboard_baler_reason_selected', {
      reason_code: code,
      reason_label: label,
      other_text: other || null,
      display_position: position,
      $survey_id: SURVEY_ID,
      $survey_submission_id: state.submissionId,
      $set_once: compact({
        cardboard_baler_primary_reason: code,
        cardboard_baler_primary_reason_label: label,
        cardboard_baler_primary_reason_other: other,
        cardboard_baler_survey_answered_at: state.answeredAt
      })
    });
  }

  function answerQ2(code, label, other, position) {
    state.stage = 'done';
    state.process = { code: code, label: label, other: other || null };
    saveState();
    sess.q2Pending = false;
    saveSess();

    var sent = {
      $survey_submission_id: state.submissionId,
      $survey_completed: true
    };
    var r = state.reason || {};
    sent['$survey_response_' + Q1_ID] = (r.code === 'other' && r.other) ? r.other : (r.label || null);
    sent['$survey_response_' + Q2_ID] = (code === 'other' && other) ? other : label;
    capture('survey sent', surveyBase(sent));

    capture('cardboard_current_process_selected', {
      process_code: code,
      process_label: label,
      other_text: other || null,
      display_position: position,
      reason_code: r.code || null,
      $survey_id: SURVEY_ID,
      $survey_submission_id: state.submissionId,
      $set_once: compact({
        cardboard_current_process: code,
        cardboard_current_process_label: label,
        cardboard_current_process_other: other
      })
    });
  }

  function showThanks() {
    finished = true;
    body.innerHTML = '';
    var p = el('p', { 'class': 'cc-bms__thanks', role: 'status', tabindex: '-1' });
    p.appendChild(el('span', { 'class': 'cc-bms__tick', 'aria-hidden': 'true' }, '✓'));
    p.appendChild(document.createTextNode(THANKS));
    body.appendChild(p);
    focusIfInside(p);
    setTimeout(teardown, THANKS_MS);
  }

  function dismiss() {
    if (finished) return;
    finished = true;
    // Picked "Something else" on Q1 but closed before Continue: Q1 is the answer
    // that matters, so keep it (no free text).
    if (root && root.dataset.otherOpen === Q1_ID && state.stage !== 'q1') {
      answerQ1('other', Q1.other[1], null, Q1.options.length + 1);
    }
    var partial = state.stage === 'q1';
    capture('survey dismissed', surveyBase(compact({
      $survey_partially_completed: partial,
      $survey_submission_id: state.submissionId || null
    })));
    if (partial) {
      sess.q2Pending = false;          // Q1 kept; do not chase Q2
    } else {
      state.dismissals = (state.dismissals || 0) + 1;
      state.lastDismissedAt = Date.now();
      saveState();
      sess.dismissed = true;
    }
    saveSess();
    teardown();
  }

  function teardown() {
    finished = true;
    if (!root) return;
    root.classList.remove('is-on');
    var r = root;
    setTimeout(function () { if (r.parentNode) r.parentNode.removeChild(r); }, 300);
  }

  // ---------------------------------------------------------------- stay out of the way

  function overlaps(a, b) {
    return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
  }

  function blocked() {
    var cls = document.body.className || '';
    // Dawn locks the page with overflow-hidden* whenever a drawer or modal is open
    // (menu drawer, cart drawer, search, product media zoom).
    if (/(^|\s)overflow-hidden/.test(cls)) return true;
    var dialogs = document.querySelectorAll('[aria-modal="true"]');
    for (var i = 0; i < dialogs.length; i++) {
      if (!root.contains(dialogs[i]) && dialogs[i].offsetParent !== null && !dialogs[i].hidden) return true;
    }
    if (started) return false;         // mid-answer: only drawers/modals take it away
    var ae = document.activeElement;
    if (ae && ae !== document.body && !root.contains(ae) && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName)) return true;
    var box = root.getBoundingClientRect();
    var avoid = document.querySelectorAll(AVOID);
    for (var j = 0; j < avoid.length; j++) {
      if (root.contains(avoid[j])) continue;
      var r = avoid[j].getBoundingClientRect();
      if (r.width && r.height && overlaps(box, r)) return true;
    }
    return false;
  }

  function sync() {
    if (!root || finished) return;
    // The chat launcher loads on its own schedule, so look for it every time.
    root.classList.toggle('cc-bms--chat', !!document.querySelector(CHAT));
    var on = !blocked();
    if (on === visible) return;
    visible = on;
    root.classList.toggle('is-on', on);
    root.setAttribute('aria-hidden', on ? 'false' : 'true');
    if (on && !sess.shownSent) {
      sess.shownSent = true;
      saveSess();
      capture('survey shown', surveyBase({}));
    }
  }

  function mount(startAtQ2) {
    if (root) return;
    build();
    if (startAtQ2) showQ2(); else showQ1();
    // Layout exists now (visibility:hidden keeps the box measurable).
    requestAnimationFrame(sync);
    setInterval(sync, 400);
    var pending = false;
    window.addEventListener('scroll', function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () { pending = false; sync(); });
    }, { passive: true });
    window.addEventListener('resize', sync);
    document.addEventListener('focusin', sync);
    document.addEventListener('focusout', function () { setTimeout(sync, 0); });
  }

  // ---------------------------------------------------------------- timing + launch gate

  function whenPosthogReady(cb) {
    var tries = 0;
    (function poll() {
      if (window.posthog && window.posthog.__loaded) return cb();
      if (++tries > 60) return;        // ~15s: blocked or failed — nothing to record into
      setTimeout(poll, 250);
    })();
  }

  function isLaunched(cb) {
    if (TEST) return cb(true);
    try {
      window.posthog.getSurveys(function (surveys) {
        var s = (surveys || []).filter(function (x) { return x.id === SURVEY_ID; })[0];
        var now = Date.now();
        cb(!!(s && s.start_date && Date.parse(s.start_date) <= now &&
              (!s.end_date || Date.parse(s.end_date) > now)));
      });
    } catch (e) { cb(false); }
  }

  // Active time counts only while the tab is visible, and carries over between
  // pages in the same tab: 40s on page A + 20s on page B = shown on page B.
  function startClock(done) {
    var last = Date.now();
    var timer = setInterval(function () {
      var now = Date.now();
      if (document.visibilityState === 'visible') {
        sess.activeMs = (sess.activeMs || 0) + Math.min(now - last, 1000);
        saveSess();
      }
      last = now;
      if ((sess.activeMs || 0) >= SHOW_AFTER_MS) {
        clearInterval(timer);
        done();
      }
    }, 250);
  }

  function initSurvey() {
    if (window.__ccWhyNowShown) return;       // just submitted a quote: leave them be
    if (!eligibleByHistory()) return;
    whenPosthogReady(function () {
      isLaunched(function (ok) {
        if (!ok) return;
        if (state.stage === 'q1' && sess.q2Pending) return mount(true);
        startClock(function () {
          if (window.__ccWhyNowShown || !eligibleByHistory()) return;
          mount(false);
        });
      });
    });
  }

  // ---------------------------------------------------------------- post-quote "why now"

  function quoteFormId() {
    if (!params || params.get('contact_posted') !== 'true') return null;
    var hash = (window.location.hash || '').replace(/^#/, '');
    if (hash) {
      for (var i = 0; i < QUOTE_FORMS.length; i++) if (hash.indexOf(QUOTE_FORMS[i]) === 0) return hash;
      return null;
    }
    // No anchor: only safe when the page has exactly one contact form (contact page).
    var forms = document.querySelectorAll('form[action^="/contact"]');
    if (forms.length === 1 && forms[0].id && QUOTE_FORMS.some(function (p) { return forms[0].id.indexOf(p) === 0; })) {
      return forms[0].id;
    }
    return null;
  }

  function successBlock(formId) {
    var form = document.getElementById(formId);
    var byAttr = document.querySelector('[data-cc-form-success="' + formId + '"]');
    if (byAttr) return byAttr;
    return form ? form.querySelector('[class*="form-success"],[class*="msg-success"]') : null;
  }

  function initWhyNow() {
    var formId = quoteFormId();
    if (!formId) return;
    var key = 'ccWhyNow:' + formId;
    try { if (sessionStorage.getItem(key)) return; } catch (e) {}
    window.__ccWhyNowShown = true;

    var tries = 0;
    (function place() {
      var s = successBlock(formId);
      // cc-form-success.js reveals the block on DOMContentLoaded; wait for it.
      if (!s || s.offsetParent === null) {
        if (++tries < 20) return setTimeout(place, 100);
        return;
      }
      injectCss();
      var box = el('div', { 'class': 'cc-whynow' });
      var taId = 'cc-whynow-' + Math.random().toString(36).slice(2, 8);
      box.appendChild(el('label', { 'class': 'cc-whynow__q', 'for': taId }, 'What made you look for a baler now?'));
      box.appendChild(el('p', { 'class': 'cc-whynow__opt' }, 'Optional'));
      // No name attribute: this sits inside the contact form on product pages and
      // must never be submitted with it.
      var ta = el('textarea', { 'class': 'cc-whynow__text', id: taId, rows: '3', maxlength: '1000', placeholder: 'Type your answer here...' });
      box.appendChild(ta);
      var go = el('button', { type: 'button', 'class': 'cc-whynow__go' }, 'Send');
      box.appendChild(go);
      go.addEventListener('click', function () {
        var text = (ta.value || '').trim();
        if (!text) { ta.focus(); return; }
        var r = state.reason || {};
        var pr = state.process || {};
        capture('cardboard_baler_why_now', {
          response_text: text,
          primary_reason: r.code || null,
          current_process: pr.code || null,
          form_id: formId,
          $survey_submission_id: state.submissionId || null
        });
        try { sessionStorage.setItem(key, '1'); } catch (e) {}
        box.innerHTML = '';
        var t = el('p', { 'class': 'cc-whynow__thanks', role: 'status' });
        t.appendChild(el('span', { 'class': 'cc-whynow__tick', 'aria-hidden': 'true' }, '✓'));
        t.appendChild(document.createTextNode(THANKS));
        box.appendChild(t);
      });
      s.parentNode.insertBefore(box, s.nextSibling);
    })();
  }

  function init() {
    initWhyNow();
    initSurvey();
  }

  window.CCBuyerSurvey = {
    state: function () { return { state: state, session: sess }; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
